import { useCallback, useEffect, useRef } from 'react';
import { useOverlayDismiss } from '../components/dismiss-gesture';
import { Portal } from '../components/Portal';

/**
 * Coquille de MODALE réutilisable (source unique de l'accessibilité des dialogues de
 * l'explorateur : sélecteur de dossier, confirmations). Rendue via `Portal` (hors des
 * ancêtres `backdrop-filter`), elle fournit :
 *  - `role="dialog"` + `aria-modal` + `aria-labelledby` (titre porté par l'appelant) ;
 *  - fermeture par ÉCHAP et par clic sur le fond (backdrop) — clic dont le GESTE ENTIER doit
 *    avoir eu lieu sur le voile, cf. `useOverlayDismiss` (XFILE-79) ;
 *  - PIÈGE DE FOCUS (Tab / Shift+Tab bouclent dans la modale) ;
 *  - focus initial (élément fourni par `initialFocusRef`, sinon le premier focusable) et
 *    RESTAURATION du focus au déclencheur à la fermeture ;
 *  - verrou de défilement du corps.
 *
 * Aligné sur le motif de `FilePreview` (même piège de focus), factorisé pour ne pas le
 * redupliquer dans chaque dialogue.
 */
export function ModalShell({
  labelledBy,
  onClose,
  children,
  initialFocusRef,
  className,
}: {
  /** `id` de l'élément titre (lié via `aria-labelledby`). */
  labelledBy: string;
  onClose: () => void;
  children: React.ReactNode;
  /** Élément à focaliser à l'ouverture (défaut : premier focusable de la modale). */
  initialFocusRef?: React.RefObject<HTMLElement | null>;
  /** Classes de la boîte de dialogue (largeur, etc.). */
  className?: string;
}): React.JSX.Element {
  const dialogRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<Element | null>(null);
  const overlayDismiss = useOverlayDismiss(onClose);

  useEffect(() => {
    restoreFocusRef.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Focus initial : l'élément demandé, sinon le premier focusable de la modale.
    if (initialFocusRef?.current) {
      initialFocusRef.current.focus();
    } else {
      dialogRef.current
        ?.querySelector<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
        )
        ?.focus();
    }
    return () => {
      document.body.style.overflow = previousOverflow;
      if (restoreFocusRef.current instanceof HTMLElement) restoreFocusRef.current.focus();
    };
  }, [initialFocusRef]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const root = dialogRef.current;
      if (!root) return;
      const focusables = root.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  return (
    <Portal>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
        {...overlayDismiss}
      >
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={labelledBy}
          onKeyDown={onKeyDown}
          className={`flex max-h-full w-full flex-col overflow-hidden rounded-lg border border-border bg-surface text-fg shadow-xl ${
            className ?? 'max-w-md'
          }`}
        >
          {children}
        </div>
      </div>
    </Portal>
  );
}
